import type { PackIaData } from "./client";

export interface ObjectiveReportMetrics {
  center: string;
  period: string;
  currency: string;
  albaranes_recibidos: number;
  pedidos_pendientes: { cantidad: number; importe_total: number };
  incidencias: { total: number; pendientes_recepcion: number; recibidos_observaciones: number };
  productos_herramientas_creados: number;
  compras: { recibido: number; no_recibido: number; total: number };
  variaciones_precios: { productos: number; mayor_porcentaje: number | null };
}

function round(value: number): number { return Math.round(value * 100) / 100; }

export function buildObjectiveReportMetrics(data: PackIaData): ObjectiveReportMetrics {
  const pendingTotal = data.pending_orders.reduce((sum, order) => sum + order.total, 0);
  const pendingReception = data.incidents.filter((incident) => incident.kind === "pending_reception").length;
  const receivedObservations = data.incidents.filter((incident) => incident.kind === "received_with_observation").length;
  const maximumVariation = data.price_variations.length
    ? Math.max(...data.price_variations.map((variation) => variation.variation_pct))
    : null;

  return {
    center: data.center,
    period: data.month_label,
    currency: data.currency,
    albaranes_recibidos: data.invoice_count,
    pedidos_pendientes: { cantidad: data.pending_orders.length, importe_total: round(pendingTotal) },
    incidencias: {
      total: pendingReception + receivedObservations,
      pendientes_recepcion: pendingReception,
      recibidos_observaciones: receivedObservations,
    },
    productos_herramientas_creados: data.created_items.length,
    compras: { recibido: data.total_received, no_recibido: data.total_not_received, total: data.total_purchased },
    variaciones_precios: { productos: data.price_variations.length, mayor_porcentaje: maximumVariation == null ? null : round(maximumVariation) },
  };
}

export function fallbackObjectiveSummary(metrics: ObjectiveReportMetrics): string {
  const currency = metrics.currency === "EUR" ? "€" : metrics.currency;
  return `${metrics.period}: se registraron ${metrics.albaranes_recibidos} albaranes recibidos, ${metrics.pedidos_pendientes.cantidad} pedidos pendientes por ${metrics.pedidos_pendientes.importe_total.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency} y ${metrics.incidencias.total} incidencias (${metrics.incidencias.pendientes_recepcion} pendientes de recepción y ${metrics.incidencias.recibidos_observaciones} recibidas con observaciones). Las compras sumaron ${metrics.compras.total.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency}, con ${metrics.productos_herramientas_creados} productos o herramientas creados y ${metrics.variaciones_precios.productos} productos con variaciones de precio.`;
}

export async function generateObjectiveSummary(data: PackIaData, signal?: AbortSignal): Promise<string> {
  const report = buildObjectiveReportMetrics(data);
  const response = await fetch("/api/summary", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ report }), signal,
  });
  const payload = await response.json() as { summary?: string; error?: string };
  if (!response.ok || !payload.summary) throw new Error(payload.error || `HTTP ${response.status}`);
  return payload.summary.trim();
}
